import {
  type EmailOtpType,
  exchangeCodeForSession,
  getSession,
  isPkceVerifierMissingError,
  verifyEmailOtp,
} from '@bangle.io/supabase';
import React, { useEffect, useState } from 'react';

function otpTypeFromParam(value: string | null): EmailOtpType | null {
  if (!value) return null;
  const allowed: EmailOtpType[] = [
    'signup',
    'invite',
    'magiclink',
    'recovery',
    'email_change',
    'email',
  ];
  return allowed.includes(value as EmailOtpType)
    ? (value as EmailOtpType)
    : null;
}

function AuthStatusCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="sn-auth-shell">
      <div className="sn-auth-stage sn-auth-stage--compact">
        <section className="sn-auth-panel">
          <h1 className="sn-auth-brand sn-auth-brand--compact">
            {t.app.pageAuth.brand}
          </h1>
          {children}
        </section>
      </div>
    </div>
  );
}

export function AuthCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const [verifiedNeedsSignIn, setVerifiedNeedsSignIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const tokenHash = params.get('token_hash');
    const type = otpTypeFromParam(params.get('type'));
    const next = params.get('next') || '/';

    void (async () => {
      try {
        // Prefer token_hash links (work across browsers/devices; no PKCE).
        if (tokenHash && type) {
          await verifyEmailOtp({ tokenHash, type });
          window.location.replace(next);
          return;
        }

        if (code) {
          try {
            await exchangeCodeForSession(code);
            window.location.replace(next);
            return;
          } catch (err) {
            // Email is usually already confirmed server-side; PKCE only
            // fails when the link was opened without the original verifier
            // (other browser, device, or origin).
            if (isPkceVerifierMissingError(err)) {
              setVerifiedNeedsSignIn(true);
              return;
            }
            throw err;
          }
        }

        const session = await getSession();
        if (!session) {
          throw new Error('No auth code or session');
        }
        window.location.replace(next);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auth callback failed');
      }
    })();
  }, []);

  if (verifiedNeedsSignIn) {
    return (
      <AuthStatusCard>
        <p className="sn-auth-status-title">
          {t.app.pageAuth.callbackVerifiedTitle}
        </p>
        <p className="sn-auth-sub">{t.app.pageAuth.callbackVerifiedBody}</p>
        <a className="sn-auth-back" href="/login">
          {t.app.pageAuth.submitSignIn}
        </a>
      </AuthStatusCard>
    );
  }

  if (error) {
    return (
      <AuthStatusCard>
        <p className="sn-auth-error" role="alert">
          {error}
        </p>
        <a className="sn-auth-back" href="/login">
          {t.app.pageAuth.callbackBack}
        </a>
      </AuthStatusCard>
    );
  }

  return (
    <AuthStatusCard>
      <p className="sn-auth-sub sn-auth-sub--solo">
        {t.app.pageAuth.callbackCompleting}
      </p>
    </AuthStatusCard>
  );
}
